import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courtApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import AnalogClock from './AnalogClock';
import CustomDropdown from './CustomDropdown';
import { useLiff } from '../hooks/useLiff';
import { getCourtMap } from '../config/courtMaps';
import BuyCreditSheet from './BuyCreditSheet';
import GiftIcon from './icons/GiftIcon';

const CourtDetails = () => {
  const { courtTypeId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, updateUserContact } = useUser();
  const { liff } = useLiff();

  // State management
  const [availabilityData, setAvailabilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCourt, setSelectedCourt] = useState('');
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('chillpay'); // 'chillpay' or 'tokens'

  // Contact information state
  const [nickname, setNickname] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [contactErrors, setContactErrors] = useState({});
  const [savingContact, setSavingContact] = useState(false);

  // Rules modal state
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [isRulesModalClosing, setIsRulesModalClosing] = useState(false);
  const [courtTypeRules, setCourtTypeRules] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [agreedToRules, setAgreedToRules] = useState(false);

  // Generate time slots dynamically based on court opening hours
  const generateTimeSlots = (openingHours) => {
    if (!openingHours) return [];

    const timeSlots = [];
    const [openHour, openMin] = openingHours.open.split(':').map(Number);
    const [closeHour, closeMin] = openingHours.close.split(':').map(Number);

    let currentHour = openHour;
    let currentMinute = openMin;

    while (currentHour < closeHour || (currentHour === closeHour && currentMinute < closeMin)) {
      timeSlots.push(`${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`);

      // Move to next 30-minute slot
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentHour += 1;
        currentMinute = 0;
      }
    }

    return timeSlots;
  };

  // Process time slots for a specific court with availability status
  const processTimeSlotsForCourt = (court) => {
    const { openingHours, bookedSlots } = court;

    // Generate time slots based on this court's opening hours
    const timeSlots = generateTimeSlots(openingHours);

    // Check if selected date is today
    const isToday = selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    return timeSlots.map(timeSlot => {
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const slotMinutes = hours * 60 + minutes;

      // Check if within opening hours
      const [openHour, openMin] = openingHours.open.split(':').map(Number);
      const [closeHour, closeMin] = openingHours.close.split(':').map(Number);
      const openMinutes = openHour * 60 + openMin;
      const closeMinutes = closeHour * 60 + closeMin;

      let status = 'closed'; // Default to closed

      if (slotMinutes >= openMinutes && slotMinutes < closeMinutes) {
        // Check if it's today and time has passed
        if (isToday && hours < currentHour) {
          status = 'closed'; // Past time
        } else if (isToday && hours === currentHour && minutes <= currentMinute) {
          status = 'closed'; // Past time
        } else if (bookedSlots.includes(timeSlot)) {
          status = 'booked';
        } else {
          status = 'available';
        }
      }

      return {
        time: timeSlot,
        status
      };
    });
  };

  // Get filtered time slots for dropdown (only available times)
  const getFilteredTimeSlots = () => {
    if (!selectedCourt || !availabilityData) return [];

    const court = availabilityData.courts.find(c => c.id === selectedCourt);
    if (!court) return [];

    const processedSlots = processTimeSlotsForCourt(court);
    return processedSlots
      .filter(slot => slot.status === 'available')
      .map(slot => slot.time);
  };

  // Get available end time slots that don't overlap with existing bookings
  const getAvailableEndTimeSlots = () => {
    if (!selectedStartTime || !selectedCourt || !availabilityData) return [];

    const court = availabilityData.courts.find(c => c.id === selectedCourt);
    if (!court) return [];

    const { bookedSlots, openingHours } = court;

    // Get all possible end times (all time slots + closing time)
    let allEndTimes = [...generateTimeSlots(openingHours)];
    if (!allEndTimes.includes(openingHours.close)) {
      allEndTimes.push(openingHours.close);
    }
    allEndTimes.sort();

    // Find the next booked slot after our start time
    // Sort booked slots to ensure we find the earliest next booking
    const startMinutes = timeToMinutes(selectedStartTime);
    const sortedBookedSlots = [...bookedSlots].sort((a, b) => {
      return timeToMinutes(a) - timeToMinutes(b);
    });

    let nextBookedStart = null;
    for (const bookedSlot of sortedBookedSlots) {
      const bookedMinutes = timeToMinutes(bookedSlot);
      if (bookedMinutes > startMinutes) {
        nextBookedStart = bookedSlot;
        break;
      }
    }

    // Return times after start time and before next booked slot (inclusive)
    return allEndTimes.filter(endTime => {
      if (endTime <= selectedStartTime) return false; // Must be after start time

      if (nextBookedStart) {
        return endTime <= nextBookedStart; // Can end at or before next booking starts
      }

      return true; // No next booking, all future times are fine
    });
  };

  // Helper function to convert time to minutes
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Validation functions for contact information
  const validateContactInfo = () => {
    const errors = {};

    if (!nickname.trim()) {
      errors.nickname = t('nickname_required');
    }

    if (!mobilePhone.trim()) {
      errors.mobilePhone = t('mobile_phone_required');
    } else {
      // Basic phone number validation (Thai format)
      const phoneRegex = /^(\+66|0)[0-9]{8,9}$/;
      if (!phoneRegex.test(mobilePhone.replace(/\s/g, ''))) {
        errors.mobilePhone = t('invalid_phone_number');
      }
    }

    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save contact information to user profile
  const saveContactInfo = async () => {
    if (!validateContactInfo()) {
      return false;
    }

    try {
      setSavingContact(true);
      await updateUserContact({
        nickname: nickname.trim(),
        mobilePhone: mobilePhone.trim()
      });
      return true;
    } catch (error) {
      console.error('Error saving contact info:', error);
      alert(t('contact_info_error'));
      return false;
    } finally {
      setSavingContact(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [courtTypeId, selectedDate]);

  // Auto-fill contact information from user data
  useEffect(() => {
    if (user) {
      setNickname(user?.contactData?.nickname || '');
      setMobilePhone(user?.contactData?.mobilePhone || '');
    }
  }, [user]);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const data = await courtApi.getCourtAvailability(courtTypeId, selectedDate);
      setAvailabilityData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError(t('failed_to_load_courts'));
      setLoading(false);
    }
  };

  // Fetch court type rules
  const fetchCourtTypeRules = async () => {
    try {
      setRulesLoading(true);
      // This will be replaced with your actual API call
      const rules = await courtApi.getCourtTypeRules(courtTypeId);
      setCourtTypeRules(rules);
      setRulesLoading(false);
    } catch (err) {
      console.error('Error fetching court type rules:', err);
      setRulesLoading(false);
    }
  };

  // Handle rules modal close
  const handleRulesModalClose = () => {
    setIsRulesModalClosing(true);
    setTimeout(() => {
      setShowRulesModal(false);
      setIsRulesModalClosing(false);
      setAgreedToRules(false);
      setSelectedEndTime('');
    }, 300);
  };

  // Handle proceed to payment after agreeing to rules
  const handleProceedToPayment = async () => {
    if (!agreedToRules) return;

    try {
      // Calculate duration in hours
      const startMinutes = timeToMinutes(selectedStartTime);
      const endMinutes = timeToMinutes(selectedEndTime);

      // Format date for API - use local timezone formatting (same as getCourtAvailability)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      // Call the booking API using the service
      const result = await courtApi.createBooking({
        idToken: user.idToken,
        courtId: selectedCourt,
        date: formattedDate,
        startTime: selectedStartTime,
        duration: endMinutes - startMinutes,
        usePoints: false,
        useTokens: selectedPaymentMethod === 'tokens',
        contactData: {
          nickname: nickname.trim(),
          mobilePhone: mobilePhone.trim()
        }
      }, liff);

      // Navigate based on payment method
      if (selectedPaymentMethod === 'tokens') {
        // Tokens payment - go directly to receipt
        navigate(`/receipt/${result.paymentId}`);
      } else {
        // ChillPay payment - redirect directly to ChillPay
        if (result.paymentUrl) {
          window.location.href = result.paymentUrl;
        } else {
          setError('Payment URL not available');
        }
      }

      setShowRulesModal(false);
      setShowBookingModal(false);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert(error.message);
    }
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const formatDate = (date) => {
    const locale = language === 'en' ? 'en-US' : 'th-TH';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long'
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isTooFarInFuture = (date) => {
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 30); // 30 days from today
    return date > maxDate;
  };

  // Check if next month navigation should be disabled
  const isNextMonthDisabled = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 30);

    // Calculate next month explicitly to avoid date overflow issues
    const currentYear = currentMonth.getFullYear();
    const currentMonthIndex = currentMonth.getMonth();
    const nextMonthIndex = currentMonthIndex + 1;
    const nextYear = nextMonthIndex > 11 ? currentYear + 1 : currentYear;
    const actualNextMonthIndex = nextMonthIndex > 11 ? 0 : nextMonthIndex;

    const firstDayOfNextMonth = new Date(nextYear, actualNextMonthIndex, 1);
    firstDayOfNextMonth.setHours(0, 0, 0, 0);

    // Disable if the first day of next month is beyond the 30-day window
    // This means no days in that month are bookable
    return firstDayOfNextMonth > maxDate;
  };

  const changeMonth = (direction) => {
    const currentYear = currentMonth.getFullYear();
    const currentMonthIndex = currentMonth.getMonth();

    let newYear, newMonthIndex;

    if (direction === 'next') {
      newMonthIndex = currentMonthIndex + 1;
      newYear = newMonthIndex > 11 ? currentYear + 1 : currentYear;
      newMonthIndex = newMonthIndex > 11 ? 0 : newMonthIndex;
    } else {
      newMonthIndex = currentMonthIndex - 1;
      newYear = newMonthIndex < 0 ? currentYear - 1 : currentYear;
      newMonthIndex = newMonthIndex < 0 ? 11 : newMonthIndex;
    }

    const newMonth = new Date(newYear, newMonthIndex, 1);

    // Don't allow navigation to months that are too far in the future
    // Check if the month has any bookable days (within 30 days from today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 30);

    // Get the first day of the new month at midnight
    const firstDayOfNewMonth = new Date(newYear, newMonthIndex, 1);
    firstDayOfNewMonth.setHours(0, 0, 0, 0);

    // Allow navigation if the first day of the month is within the 30-day window
    // This means at least some days in that month are bookable
    if (firstDayOfNewMonth <= maxDate) {
      setCurrentMonth(newMonth);
    }
  };

  const handleDateSelect = (date) => {
    if (!isPastDate(date) && !isTooFarInFuture(date)) {
      setSelectedDate(date);
      // Reset time selections when date changes
      setSelectedStartTime('');
      setSelectedEndTime('');
    }
  };

  const handleCourtChange = (courtId) => {
    setSelectedCourt(courtId);
    // Reset time selections when court changes
    setSelectedStartTime('');
    setSelectedEndTime('');
  };

  const handleEndTimeChange = (time) => {
    setSelectedEndTime(time);
    if (time) {
      // Show booking modal when end time is selected
      setShowBookingModal(true);
    }
  };

  const handleConfirmBooking = async () => {
    // Save contact information first
    const contactSaved = await saveContactInfo();
    if (!contactSaved) {
      return; // Don't proceed if contact info couldn't be saved
    }

    // Close booking modal and show rules modal
    setShowBookingModal(false);
    setShowRulesModal(true);
    // Fetch rules when modal opens
    fetchCourtTypeRules();
  };

  const handleCancelBooking = () => {
    setIsModalClosing(true);
    // Wait for animation to complete before hiding modal
    setTimeout(() => {
      setShowBookingModal(false);
      setIsModalClosing(false);
      setSelectedEndTime('');
    }, 300); // Match animation duration
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white">
          <div className="w-12 h-12 border-4 border-white border-opacity-30 border-t-white rounded-full animate-spin mb-5"></div>
          <p className="text-xl">{t('loading_data')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white text-center">
          <p className="text-xl mb-5">{error}</p>
          <button
            onClick={fetchAvailability}
            className="bg-white bg-opacity-20 border-2 border-white text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-indigo-500"
          >
            {t('try_again')}
          </button>
        </div>
      </div>
    );
  }

  const days = getDaysInMonth(currentMonth);
  // Get court type name from availability data if available
  const courtName = availabilityData?.courts?.[0]?.courtTypeName || 'Court';

  return (
    <div className="min-h-screen pt-40 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-5">
      {/* Custom CSS for rules styling */}
      <style jsx>{`
        /* Modern minimal bullet styling for rules */
        .rules-content ul {
          list-style: none;
          padding-left: 0;
          margin: 0;
        }
        
        .rules-content li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
          line-height: 1.5;
          color: #374151;
        }
        
        .rules-content li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.5rem;
          width: 4px;
          height: 4px;
          background-color: #6366f1;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
        }
        
        .rules-content li:last-child {
          margin-bottom: 0;
        }
      `}</style>
      {/* Header */}
      <div className="flex items-center mb-6 text-white">
        <button
          className="bg-white bg-opacity-20 border-none text-white px-5 py-3 rounded-full text-lg cursor-pointer mr-5 transition-all duration-300 hover:bg-opacity-30"
          onClick={() => navigate('/')}
          aria-label={t('back')}
        >
          ← {t('back')}
        </button>
        <h1 className="text-3xl md:text-4xl font-bold">{t('book')}</h1>
      </div>

      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6">
        {/* Court Map — plain image at the top (rounded to match the cards), tap to enlarge */}
        <button
          type="button"
          onClick={() => setMapOpen(true)}
          className="block w-full"
          aria-label={t('court_map')}
        >
          <img
            src={getCourtMap(availabilityData?.courts?.[0]?.courtTypeName)}
            alt=""
            loading="lazy"
            className="w-full h-auto rounded-3xl"
          />
        </button>

        {/* Date Picker */}
        <div className="bg-white bg-opacity-95 rounded-3xl p-3 sm:p-6">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <button
              onClick={() => changeMonth('prev')}
              className="w-8 h-8 sm:w-12 sm:h-12 bg-indigo-500 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-bold hover:bg-indigo-600 transition-colors"
            >
              ‹
            </button>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">{formatDate(currentMonth)}</h2>
            <button
              onClick={() => changeMonth('next')}
              disabled={isNextMonthDisabled()}
              className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-lg sm:text-2xl font-bold transition-colors ${isNextMonthDisabled()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-500 text-white hover:bg-indigo-600'
                }`}
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {/* Day headers */}
            {language === 'th'
              ? ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map(day => (
                <div key={day} className="text-center py-1 sm:py-2 text-gray-600 font-medium text-xs sm:text-sm">
                  {day}
                </div>
              ))
              : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center py-1 sm:py-2 text-gray-600 font-medium text-xs sm:text-sm">
                  {day}
                </div>
              ))
            }

            {/* Calendar days */}
            {days.map((day, index) => (
              <div key={index} className="text-center">
                {day ? (
                  <button
                    onClick={() => handleDateSelect(day)}
                    disabled={isPastDate(day) || isTooFarInFuture(day)}
                    className={`
                      w-8 h-8 sm:w-12 sm:h-12 rounded-full text-sm sm:text-lg font-medium transition-all duration-200
                      ${isSelected(day)
                        ? 'bg-indigo-500 text-white shadow-lg'
                        : isToday(day)
                          ? 'bg-indigo-100 text-indigo-600 border-2 border-indigo-300'
                          : isPastDate(day) || isTooFarInFuture(day)
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-indigo-100 hover:text-indigo-600'
                      }
                    `}
                  >
                    {day.getDate()}
                  </button>
                ) : (
                  <div className="w-8 h-8 sm:w-12 sm:h-12"></div>
                )}
              </div>
            ))}
          </div>

          {/* Booking limit notice */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700 text-center">
              <span className="font-medium">📅 {t('note')}:</span> {t('booking_limit_30_days')}
            </p>
          </div>
        </div>

        {/* Court and Time Selection */}
        <div className="bg-white bg-opacity-95 rounded-3xl p-3 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Court Selection */}
            <div>
              <label className="block text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                {t('select_court')}
              </label>
              <CustomDropdown
                value={selectedCourt}
                onChange={handleCourtChange}
                placeholder={t('please_select_court_dropdown')}
                options={(availabilityData?.courts || []).map((court) => ({
                  value: court.id,
                  label: court.name,
                }))}
              />
            </div>

            {/* Time Selection */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                  {t('select_start_time')}
                </label>
                <CustomDropdown
                  value={selectedStartTime}
                  onChange={setSelectedStartTime}
                  placeholder={t('please_select_start_time')}
                  disabled={!selectedCourt || availabilityData?.closed}
                  options={getFilteredTimeSlots().map((time) => ({
                    value: time,
                    label: time,
                  }))}
                />
              </div>

              <div>
                <label className="block text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                  {t('select_end_time')}
                </label>
                <CustomDropdown
                  value={selectedEndTime}
                  onChange={handleEndTimeChange}
                  placeholder={t('please_select_end_time')}
                  disabled={!selectedStartTime || availabilityData?.closed}
                  options={getAvailableEndTimeSlots().map((time) => ({
                    value: time,
                    label: time,
                  }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Court Map — full-screen lightbox */}
        {mapOpen && (
          <div
            onClick={() => setMapOpen(false)}
            className="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center p-3 overflow-auto"
          >
            <button
              onClick={() => setMapOpen(false)}
              aria-label={t('close')}
              className="absolute top-4 right-4 text-white text-opacity-90 hover:text-opacity-100"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={getCourtMap(availabilityData?.courts?.[0]?.courtTypeName)}
              alt={t('court_map')}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        )}

        {/* Availability Grid */}
        <div className="bg-white bg-opacity-95 rounded-3xl p-4 sm:p-6">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">
            {t('booking_status')} - {formatDate(selectedDate)}
          </h3>

          {availabilityData && availabilityData.closed ? (
            <div className="text-center py-10 px-4 bg-red-50 border border-red-200 rounded-2xl">
              <div className="text-4xl mb-3">🚧</div>
              <p className="text-lg font-bold text-red-700">{t('closed_this_date')}</p>
              {availabilityData.closureReason && (
                <p className="text-sm text-red-600 mt-1">{availabilityData.closureReason}</p>
              )}
              <p className="text-sm text-gray-500 mt-2">{t('closed_no_booking')}</p>
            </div>
          ) : availabilityData ? (
            <div className="overflow-x-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-6 min-w-max">
                {availabilityData.courts.map((court, index) => (
                  <AnalogClock
                    key={court.id}
                    court={court}
                    selectedDate={selectedDate}
                    timeSlotsData={processTimeSlotsForCourt(court)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-indigo-500 border-opacity-30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('loading_booking_data')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Booking Confirmation Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            onClick={handleCancelBooking}
          ></div>

          {/* Modal */}
          <div className={`relative w-full max-w-md max-h-[90vh] sm:max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl transform transition-all duration-300 ease-out sm:scale-100 ${isModalClosing ? 'animate-slide-down' : 'animate-slide-up'}`}>
            {/* Handle for mobile */}
            <div className="flex justify-center pt-4 pb-2 sm:hidden">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(85vh-80px)]">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                {t('confirm_booking')}
              </h3>

              {/* Booking Details */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-600">{t('court')}</span>
                  <span className="font-semibold text-gray-800">
                    {availabilityData?.courts?.find(c => c.id === selectedCourt)?.name || t('not_specified')}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-600">{t('date')}</span>
                  <span className="font-semibold text-gray-800">
                    {selectedDate.toLocaleDateString(language === 'en' ? 'en-US' : 'th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-600">{t('time')}</span>
                  <span className="font-semibold text-gray-800">
                    {selectedStartTime} - {selectedEndTime}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-600">{t('cost')}</span>
                  <span className="font-semibold text-gray-800">
                    ฿{(() => {
                      const court = availabilityData?.courts?.find(c => c.id === selectedCourt);
                      const costPerSlot = court?.costPerSlot || 0;
                      const startMinutes = timeToMinutes(selectedStartTime);
                      const endMinutes = timeToMinutes(selectedEndTime);
                      const numberOfSlots = (endMinutes - startMinutes) / 30;
                      return costPerSlot * numberOfSlots;
                    })()}
                  </span>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                  {t('contact_information')}
                </h4>

                <div className="space-y-4">
                  {/* Nickname Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('nickname')} *
                    </label>
                    <input
                      type="text"
                      value={nickname}
                      onChange={(e) => {
                        setNickname(e.target.value);
                        if (contactErrors.nickname) {
                          setContactErrors(prev => ({ ...prev, nickname: '' }));
                        }
                      }}
                      placeholder={t('enter_nickname')}
                      className={`w-full p-3 border-2 rounded-2xl text-base focus:outline-none transition-colors ${contactErrors.nickname
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:border-indigo-500'
                        }`}
                    />
                    {contactErrors.nickname && (
                      <p className="text-red-500 text-sm mt-1">{contactErrors.nickname}</p>
                    )}
                  </div>

                  {/* Mobile Phone Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('mobile_phone')} *
                    </label>
                    <input
                      type="tel"
                      value={mobilePhone}
                      onChange={(e) => {
                        setMobilePhone(e.target.value);
                        if (contactErrors.mobilePhone) {
                          setContactErrors(prev => ({ ...prev, mobilePhone: '' }));
                        }
                      }}
                      placeholder={t('enter_mobile_phone')}
                      className={`w-full p-3 border-2 rounded-2xl text-base focus:outline-none transition-colors ${contactErrors.mobilePhone
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:border-indigo-500'
                        }`}
                    />
                    {contactErrors.mobilePhone && (
                      <p className="text-red-500 text-sm mt-1">{contactErrors.mobilePhone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                  {t('select_payment_method')}
                </h4>

                <div className="space-y-3">
                  {/* ChillPay Payment Option */}
                  <div
                    className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${selectedPaymentMethod === 'chillpay'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    onClick={() => setSelectedPaymentMethod('chillpay')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === 'chillpay'
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300 bg-white'
                          }`}>
                          {selectedPaymentMethod === 'chillpay' && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{t('qr_code_payment')}</p>
                        </div>
                      </div>
                      <span className="font-semibold text-gray-800">
                        ฿{(() => {
                          const court = availabilityData?.courts?.find(c => c.id === selectedCourt);
                          const costPerSlot = court?.costPerSlot || 0;
                          const startMinutes = timeToMinutes(selectedStartTime);
                          const endMinutes = timeToMinutes(selectedEndTime);
                          const numberOfSlots = (endMinutes - startMinutes) / 30;
                          return costPerSlot * numberOfSlots;
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Tokens Payment Option */}
                  {(() => {
                    const court = availabilityData?.courts?.find(c => c.id === selectedCourt);
                    const have = user?.tokens?.[court?.courtTypeId] || 0;
                    const need = (timeToMinutes(selectedEndTime) - timeToMinutes(selectedStartTime)) / 60;
                    const enough = have > 0 && have >= need;
                    return (
                      <div
                        className={`relative overflow-hidden p-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${selectedPaymentMethod === 'tokens'
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        onClick={() => { enough ? setSelectedPaymentMethod('tokens') : setBuyOpen(true); }}
                      >
                        <div className={`flex items-center justify-between ${enough ? '' : 'opacity-50'}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === 'tokens'
                                ? 'border-indigo-500 bg-indigo-500'
                                : 'border-gray-300 bg-white'
                              }`}>
                              {selectedPaymentMethod === 'tokens' && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-800">{t('tokens_payment') || 'Tokens Payment'}</p>
                              <p className="text-sm text-gray-600">
                                {have > 0
                                  ? `${t('use_tokens') || 'Use tokens'} (${have.toFixed(1)} ${t('remaining_tokens') || 'remaining'})`
                                  : t('insufficient_tokens') || 'Insufficient tokens'}
                              </p>
                            </div>
                          </div>
                          <span className="font-semibold text-gray-800">
                            {need.toFixed(1)} {t('tokens') || 'tokens'}
                          </span>
                        </div>

                        {/* Top-up band — only when short; whole card opens the sheet, so this is just the visual cue */}
                        {!enough && (
                          <div className="-mx-4 -mb-4 mt-3 flex items-center justify-center gap-1.5 bg-gradient-to-br from-fuchsia-500 to-purple-600 py-2.5 text-sm font-bold text-white rounded-b-2xl pointer-events-none">
                            <GiftIcon className="w-4 h-4" />
                            {t('promotion') || 'Promotion'}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {/* <button
                    onClick={handleCancelBooking}
                    className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-2xl font-semibold transition-all duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button> */}
                <button
                  onClick={handleConfirmBooking}
                  disabled={savingContact}
                  className={`flex-1 px-6 py-4 rounded-2xl font-semibold transition-all duration-200 ${savingContact
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-indigo-500 text-white hover:bg-indigo-600'
                    }`}
                >
                  {savingContact ? t('saving_contact_info') : t('confirm_and_pay')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rules and Confirm Modal */}
      {showRulesModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            onClick={handleRulesModalClose}
          ></div>

          {/* Modal */}
          <div className={`relative w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl transform transition-all duration-300 ease-out sm:scale-100 ${isRulesModalClosing ? 'animate-slide-down' : 'animate-slide-up'}`}>
            {/* Handle for mobile */}
            <div className="flex justify-center pt-4 pb-2 sm:hidden">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(85vh-80px)]">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                {t('rules_and_terms')}
              </h3>

              {/* Rules Content */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">{t('court_rules')}</h4>

                {rulesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin"></div>
                    <span className="ml-3 text-gray-600">{t('loading_rules')}</span>
                  </div>
                ) : courtTypeRules && (courtTypeRules.thai || courtTypeRules.english) ? (
                  <div className="bg-gray-50 rounded-2xl p-4 max-h-48 sm:max-h-64 overflow-y-auto">
                    <div className="prose prose-sm max-w-none rules-content">
                      <div dangerouslySetInnerHTML={{
                        __html: language === 'th' ? courtTypeRules.thai : courtTypeRules.english
                      }} />
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-2xl p-4 text-center text-gray-500">
                    {t('no_rules_available')}
                  </div>
                )}
              </div>

              {/* Agreement Checkbox */}
              <div className="mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToRules}
                    onChange={(e) => setAgreedToRules(e.target.checked)}
                    className="mt-1 w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    {t('i_have_read_and_understand')} <span className="font-semibold text-indigo-600">{t('court_rules')}</span> {t('and_agree_to_comply')}
                  </span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRulesModalClose}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-2xl font-semibold transition-all duration-200 hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleProceedToPayment}
                  disabled={!agreedToRules}
                  className={`flex-1 px-6 py-4 rounded-2xl font-semibold transition-all duration-200 ${agreedToRules
                      ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {t('proceed_to_payment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BuyCreditSheet
        open={buyOpen}
        defaultCourtTypeId={courtTypeId}
        idToken={user?.idToken}
        liff={liff}
        userTokens={user?.tokens}
        onClose={() => setBuyOpen(false)}
      />
    </div>
  );
};

export default CourtDetails; 
